import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookingModal } from '../components/shared/BookingModal';
import { LeadForm } from '../components/shared/LeadForm';
import { ArrowRight, CheckCircle, MapPin, Globe, Sparkles, Search, Code, Star, TrendingUp, Users } from 'lucide-react';

const servicesData = {
    'gmb-optimization-services': {
        icon: MapPin,
        name: 'GMB / GBP Optimization',
        headline: 'Dominate Google Maps & Local Search',
        subheadline: 'Get your business to #1 in Google Maps and capture customers actively searching for your services nearby.',
        description: 'Google Business Profile (formerly GMB) is the single most important factor for local businesses. When someone searches "near me" or for services in your area, your GBP is what shows up. We optimize every aspect of your profile to ensure you rank #1 in the map pack.',
        features: [
            'Complete profile optimization (name, categories, attributes)',
            'Compelling business description with keywords',
            'Photo and video optimization (cover, logo, interior, team)',
            'Review generation and response system',
            'Weekly posts and updates',
            'Q&A management',
            'Local citation building (100+ directories)',
            'NAP consistency audit and fixes',
            'Competitor monitoring and tracking',
            'Monthly ranking and performance reports',
        ],
        benefits: [
            { title: 'Appear in 3-Pack', desc: 'Rank in the top 3 map results for your key services' },
            { title: 'More Phone Calls', desc: 'Direct calls from customers ready to buy' },
            { title: 'Increased Foot Traffic', desc: 'Direction requests from nearby searchers' },
            { title: 'Trust & Credibility', desc: 'Build authority through reviews and engagement' },
        ],
        stats: { traffic: '+200%', calls: '+150%', roi: '6x' },
        faqs: [
            { q: 'How long does it take to rank in the map pack?', a: 'Most businesses see significant improvement within 2-3 months, with top 3 rankings achievable in 3-6 months depending on competition.' },
            { q: 'Do you handle review management?', a: 'Yes! We set up review generation systems and respond to all reviews professionally on your behalf.' },
            { q: 'Can you help with multiple locations?', a: 'Absolutely. We specialize in multi-location GMB optimization with consistent branding and location-specific strategies.' },
        ],
        color: 'from-red-500 to-orange-500',
    },
    'geo-seo-services': {
        icon: Globe,
        name: 'GEO SEO',
        headline: 'Rank for Every Location You Serve',
        subheadline: 'Capture customers searching for your services + city name with targeted location pages.',
        description: 'GEO SEO (Geographic SEO) targets searches like "dentist in Delhi" or "plumber near Noida". We create and optimize location-specific landing pages that rank for these high-intent, location-based searches.',
        features: [
            'Location keyword research and mapping',
            'City-specific landing page creation',
            'Local schema markup implementation',
            'Location-specific content writing',
            'Internal linking optimization',
            'Local backlink building',
            'NAP consistency across all pages',
            'Google Maps embedding',
            'Service area page optimization',
            'Multi-language location pages',
        ],
        benefits: [
            { title: 'City Dominance', desc: 'Rank #1 for [service] + [city] searches' },
            { title: 'Scalable Reach', desc: 'Cover all cities and neighborhoods you serve' },
            { title: 'Higher Intent', desc: 'Capture users actively looking in specific areas' },
            { title: 'Competitive Edge', desc: 'Outrank competitors in their own areas' },
        ],
        stats: { cities: '50+', traffic: '+300%', leads: '+180%' },
        faqs: [
            { q: 'How many location pages do I need?', a: 'It depends on your service area. We typically recommend starting with your top 10-20 cities and expanding based on results.' },
            { q: 'Won\'t duplicate content be a problem?', a: 'No. We create unique, valuable content for each location with local data, testimonials, and specific information.' },
            { q: 'Can this work for service-area businesses?', a: 'Yes! GEO SEO is perfect for businesses that travel to customers, like plumbers, electricians, and home services.' },
        ],
        color: 'from-green-500 to-emerald-500',
    },
    'ai-search-optimization': {
        icon: Sparkles,
        name: 'AI Search Optimization',
        headline: 'Get Found in ChatGPT, SGE & Perplexity',
        subheadline: 'Future-proof your visibility as AI transforms how people search for information.',
        description: 'AI is changing search. ChatGPT, Google SGE, Perplexity, and other AI assistants are becoming the first stop for many searchers. We optimize your content and brand presence to be cited and recommended by AI systems.',
        features: [
            'AI content optimization strategies',
            'Entity and knowledge graph building',
            'Structured data enhancement',
            'Authority signal development',
            'Conversational query optimization',
            'Brand mention monitoring',
            'AI citation tracking',
            'Wikipedia and authority site presence',
            'Expert quote and attribution building',
            'AI search performance reporting',
        ],
        benefits: [
            { title: 'AI Citations', desc: 'Get mentioned when AI answers relevant queries' },
            { title: 'First Mover Advantage', desc: 'Establish authority before competitors catch on' },
            { title: 'Future-Proof SEO', desc: 'Adapt to the changing search landscape' },
            { title: 'Brand Authority', desc: 'Build recognition as an industry expert' },
        ],
        stats: { mentions: '500+', visibility: '+400%', authority: '10x' },
        faqs: [
            { q: 'How do you track AI search visibility?', a: 'We monitor AI responses for your key topics, track brand mentions, and measure citation frequency across major AI platforms.' },
            { q: 'Does traditional SEO still matter?', a: 'Absolutely. AI search and traditional SEO work together. Strong traditional SEO often correlates with AI visibility.' },
            { q: 'Which AI platforms do you optimize for?', a: 'We focus on ChatGPT, Google SGE, Perplexity, Bing AI, and emerging platforms as they gain traction.' },
        ],
        color: 'from-purple-500 to-pink-500',
    },
    'seo-services': {
        icon: Search,
        name: 'High-Intent SEO',
        headline: 'Rank for Keywords That Actually Convert',
        subheadline: 'Target commercial keywords that bring ready-to-buy customers, not just traffic.',
        description: 'Not all traffic is created equal. We focus on high-intent, bottom-of-funnel keywords that indicate buying intent. These are the searches made by people ready to take action, not just browse.',
        features: [
            'Commercial keyword research',
            'Competitor keyword gap analysis',
            'On-page SEO optimization',
            'Technical SEO audit and fixes',
            'Content strategy and creation',
            'Link building campaigns',
            'Conversion rate optimization',
            'Landing page optimization',
            'ROI tracking and attribution',
            'Monthly strategy calls',
        ],
        benefits: [
            { title: 'Revenue Focus', desc: 'Target keywords that lead to sales, not vanity metrics' },
            { title: 'Qualified Traffic', desc: 'Attract visitors ready to convert' },
            { title: 'Measurable ROI', desc: 'Track revenue generated from SEO efforts' },
            { title: 'Sustainable Growth', desc: 'Build long-term organic acquisition channels' },
        ],
        stats: { keywords: '200+', conversion: '+120%', roi: '8x' },
        faqs: [
            { q: 'How do you identify high-intent keywords?', a: 'We analyze search intent, look for commercial modifiers (buy, best, price, near me), and study your conversion data to find the money keywords.' },
            { q: 'How long until I see ROI?', a: 'Most clients see positive ROI within 4-6 months, with compounding returns as rankings stabilize.' },
            { q: 'Do you guarantee rankings?', a: 'We don\'t guarantee specific rankings (no ethical agency does), but we guarantee our methodology and effort towards results.' },
        ],
        color: 'from-blue-500 to-cyan-500',
    },
    'programmatic-seo': {
        icon: Code,
        name: 'Programmatic SEO',
        headline: 'Scale to Thousands of Optimized Pages',
        subheadline: 'Automatically generate SEO-optimized pages to capture massive long-tail traffic.',
        description: 'Programmatic SEO allows you to create hundreds or thousands of targeted pages efficiently. Perfect for businesses with large inventories, service areas, or data-driven content opportunities.',
        features: [
            'Scalable page template design',
            'Data source integration',
            'Automated content generation',
            'Dynamic internal linking',
            'Quality control systems',
            'Indexation management',
            'Performance monitoring',
            'Template iteration and optimization',
            'Duplicate content prevention',
            'Scale roadmap planning',
        ],
        benefits: [
            { title: 'Massive Scale', desc: 'Create 1000+ pages efficiently' },
            { title: 'Long-tail Capture', desc: 'Rank for thousands of niche keywords' },
            { title: 'Cost Efficiency', desc: 'Lower cost per page than manual creation' },
            { title: 'Quick Deployment', desc: 'Launch hundreds of pages in weeks' },
        ],
        stats: { pages: '1000+', keywords: '10K+', traffic: '+500%' },
        faqs: [
            { q: 'Is programmatic SEO just spam?', a: 'No! Done right, programmatic SEO creates genuinely useful pages. The key is providing unique value on each page, not just churning out thin content.' },
            { q: 'What kind of data do I need?', a: 'Any structured data works: locations, products, services, stats, comparisons. We help identify the best data sources for your business.' },
            { q: 'How do you ensure quality at scale?', a: 'We use quality control checklists, manual reviews of templates, and continuous monitoring to maintain standards.' },
        ],
        color: 'from-amber-500 to-yellow-500',
    },
};

export default function ServiceLandingPage() {
    const location = useLocation();
    const serviceSlug = location.pathname.slice(1); // Remove leading slash
    const [bookingOpen, setBookingOpen] = useState(false);
    
    const service = servicesData[serviceSlug];
    
    if (!service) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
                    <Button asChild className="rounded-full">
                        <Link to="/services">View All Services</Link>
                    </Button>
                </div>
            </div>
        );
    }

    const ServiceIcon = service.icon;

    return (
        <div className="relative" data-testid={`service-page-${serviceSlug}`}>
            {/* Hero */}
            <section className="section-padding relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-5`} />
                <div className="container-custom relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                                <ServiceIcon className="w-4 h-4" />
                                <span>{service.name}</span>
                            </div>
                            
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                                {service.headline}
                            </h1>
                            
                            <p className="text-lg md:text-xl text-muted-foreground mb-8">
                                {service.subheadline}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                <Button 
                                    size="lg"
                                    className="btn-glow rounded-full px-8"
                                    onClick={() => setBookingOpen(true)}
                                    data-testid="service-hero-cta"
                                >
                                    Get Started
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <Button 
                                    variant="outline"
                                    size="lg"
                                    asChild
                                    className="rounded-full px-8"
                                >
                                    <a href="#features">See What's Included</a>
                                </Button>
                            </div>

                            {/* Quick Stats */}
                            <div className="flex flex-wrap gap-6">
                                {Object.entries(service.stats).map(([key, value]) => (
                                    <div key={key}>
                                        <div className="text-2xl font-bold text-primary">{value}</div>
                                        <div className="text-sm text-muted-foreground capitalize">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Form */}
                        <div className="lg:pl-8">
                            <div className="p-8 rounded-3xl border border-border bg-background shadow-xl">
                                <h3 className="text-xl font-semibold mb-2">Get a Free Audit</h3>
                                <p className="text-muted-foreground mb-6 text-sm">
                                    See how your business can improve with {service.name}.
                                </p>
                                <LeadForm sourcePage={serviceSlug} compact />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Description */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-3xl">
                        <h2 className="text-3xl font-semibold tracking-tight mb-6">
                            Why {service.name}?
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            {service.description}
                        </p>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="section-padding">
                <div className="container-custom">
                    <h2 className="text-3xl font-semibold tracking-tight mb-12 text-center">
                        Key Benefits
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {service.benefits.map((benefit, index) => (
                            <div 
                                key={index}
                                className="p-6 rounded-2xl border border-border bg-background hover:border-primary/30 transition-colors"
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                                    <CheckCircle className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                                <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="section-padding bg-muted/30 scroll-mt-24">
                <div className="container-custom">
                    <h2 className="text-3xl font-semibold tracking-tight mb-4 text-center">
                        What's Included
                    </h2>
                    <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                        Everything you need for comprehensive {service.name.toLowerCase()} success.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                        {service.features.map((feature, index) => (
                            <div 
                                key={index}
                                className="flex items-center gap-3 p-4 rounded-xl bg-background border border-border"
                            >
                                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Social Proof */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-semibold tracking-tight mb-6">
                                Trusted by Growing Businesses
                            </h2>
                            <p className="text-muted-foreground mb-8">
                                Join 300+ businesses that have transformed their search visibility with Aitrixy.
                            </p>
                            
                            <div className="space-y-4">
                                {[
                                    { icon: Star, text: '4.9/5 average client rating' },
                                    { icon: TrendingUp, text: '98% client retention rate' },
                                    { icon: Users, text: '300+ businesses served' },
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <item.icon className="w-5 h-5 text-primary" />
                                        </div>
                                        <span>{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-8 rounded-3xl bg-foreground text-background">
                            <div className="flex gap-1 mb-6">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                                ))}
                            </div>
                            <p className="text-lg mb-6">
                                "Aitrixy's {service.name.toLowerCase()} service transformed our online presence. 
                                We saw measurable results within the first 2 months."
                            </p>
                            <div>
                                <div className="font-semibold">Happy Client</div>
                                <div className="text-sm text-background/60">Verified Business Owner</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <h2 className="text-3xl font-semibold tracking-tight mb-12 text-center">
                        Frequently Asked Questions
                    </h2>
                    <div className="max-w-3xl mx-auto space-y-4">
                        {service.faqs.map((faq, index) => (
                            <div 
                                key={index}
                                className="p-6 rounded-2xl border border-border bg-background"
                            >
                                <h3 className="font-semibold mb-2">{faq.q}</h3>
                                <p className="text-muted-foreground">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className={`rounded-3xl bg-gradient-to-br ${service.color} p-px`}>
                        <div className="rounded-3xl bg-foreground p-12 md:p-16 text-center text-background">
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">
                                Ready to Get Started?
                            </h2>
                            <p className="text-lg text-background/70 max-w-2xl mx-auto mb-8">
                                Book a free strategy call and see how {service.name.toLowerCase()} can 
                                transform your business.
                            </p>
                            <Button 
                                size="lg"
                                className="bg-background text-foreground hover:bg-background/90 rounded-full px-8"
                                onClick={() => setBookingOpen(true)}
                                data-testid="service-bottom-cta"
                            >
                                Book Free Consultation
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
